import { CircularProgress, Stack, Typography } from '@mui/material';
import { api } from '@verifime/api-definition';
import {
  AddOrganisationNotification,
  DisplayAlertErrors,
  enqueErrorNotification,
  enqueSuccessNotification,
  OrganisationNotificationList,
  TAddOrganisationNotificationFromData,
  TOrganisationNotification,
  TOrganisationNotificationType,
} from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { useEffect, useState } from 'react';

export const OrganisationNotification = ({
  organisationId,
  allowAdd = true,
}: {
  organisationId: string;
  allowAdd?: boolean;
}) => {
  const [notificationList, setNotificationList] = useState<TOrganisationNotification[]>([]);
  const [isLoadingNotificationList, setIsLoadingNotificationList] = useState(true);
  const [loadNotificationError, setLoadNotificationError] = useState<string | null>(null);

  useEffect(() => {
    if (!organisationId) {
      return;
    }

    setIsLoadingNotificationList(true);
    api
      .getV1organisationOrganisationIdnotificationpreferences({ params: { organisationId } })
      .then((notifications) => {
        setNotificationList(notifications as TOrganisationNotification[]);
      })
      .catch((error) => {
        setLoadNotificationError(error?.message ?? 'Unknown error');
      })
      .finally(() => {
        setIsLoadingNotificationList(false);
      });
  }, [organisationId]);

  const activeNotificationList = notificationList.filter(
    (notification) => notification.status === 'ACTIVE',
  );

  const handleAddNotification = async ({ detail, type }: TAddOrganisationNotificationFromData) => {
    return api
      .postV1organisationOrganisationIdnotificationpreferencestypeType(
        { detail },
        { params: { organisationId, type: type as TOrganisationNotificationType } },
      )
      .then((notification) => {
        setNotificationList((prevNotificaitonList) => [
          ...prevNotificaitonList,
          notification as TOrganisationNotification,
        ]);
        enqueSuccessNotification('Added');
      })
      .catch((error) => enqueErrorNotification(error));
  };

  const handleRemoveNotification = (id: string) => {
    api
      .patchV1organisationnotificationpreferencesPreferenceIddeactivate(undefined, {
        params: { preferenceId: id },
      })
      .then(() => {
        setNotificationList((prevNotificationList) =>
          prevNotificationList.filter((notification) => notification.id !== id),
        );
        enqueSuccessNotification('Removed');
      })
      .catch((error) => enqueErrorNotification(error));
  };

  return (
    <Stack gap={tokens.spacingXs} mt={tokens.spacingBase}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <Typography variant="h5">Notification</Typography>
        {allowAdd && <AddOrganisationNotification onAddNotification={handleAddNotification} />}
      </Stack>
      {isLoadingNotificationList && <CircularProgress size={20} />}
      {loadNotificationError && (
        <DisplayAlertErrors errors={[{ message: loadNotificationError }]} />
      )}
      {!isLoadingNotificationList && (
        <OrganisationNotificationList
          notifications={activeNotificationList}
          onRemoveNotification={handleRemoveNotification}
        />
      )}
    </Stack>
  );
};
